/**
 * Name: dom.js
 * ES6 Module for DOM Manipulation Utilities
 * Description: Provides helper functions for common DOM operations such as querying, creating, inserting elements, showing/hiding elements, and event handling with delegation.
 * license: GNU General Public License v2 or later
 */

// --------------------------------------------------
// DOM Manipulation Utility Functions
// --------------------------------------------------

/**
 * Query single element
 * @param {string} selector 
 * @param {HTMLElement|Document} root 
 * @returns {HTMLElement|null}
 */
export const $ = (selector, root = document) => root.querySelector(selector);

/**
 * Query multiple elements as Array
 * @param {string} selector 
 * @param {HTMLElement|Document} root 
 * @returns {HTMLElement[]}
 */
export const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

/**
 * Create element from HTML string
 * @param {string} html 
 * @returns {HTMLElement}
 */
export const create = (html) => {
  const template = document.createElement('template');
  template.innerHTML = html.trim();
  return template.content.firstElementChild;
};

/**
 * Insert HTML string into root
 * @param {HTMLElement} root 
 * @param {string} html 
 * @param {'beforebegin'|'afterbegin'|'beforeend'|'afterend'} where 
 */
export const insertHTML = (root, html, where = 'beforeend') => root.insertAdjacentHTML(where, html);

/**
 * Show element
 * @param {HTMLElement} el 
 */
export const show = (el) => { if (el) el.style.display = ''; };

/**
 * Hide element
 * @param {HTMLElement} el 
 */
export const hide = (el) => { if (el) el.style.display = 'none'; };

/**
 * Add event listener with optional delegation
 * @param {HTMLElement} el 
 * @param {string} event 
 * @param {string|function} selectorOrHandler - selector string for delegation or handler
 * @param {function} [handler] - handler function if using delegation
 * @returns {function} - function to remove listener
 */
export const on = (el, event, selectorOrHandler, handler) => {
  if (typeof selectorOrHandler === 'function') {
    el.addEventListener(event, selectorOrHandler);
    return () => el.removeEventListener(event, selectorOrHandler);
  }

  const selector = selectorOrHandler;
  const listener = (e) => {
    const t = e.target.closest(selector);
    if (t && el.contains(t)) handler.call(t, e);
  };
  el.addEventListener(event, listener);
  return () => el.removeEventListener(event, listener);
};

// --------------------------------------------------
// Backwards compatibility
// --------------------------------------------------
export const createFromHTML = create;
